import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  ChangeOrganizationAdminActiveOrganizationCommand,
  GetOrganizationAdminOrganizationsQueryResult,
  OrganizationAdminsService,
  RoleType,
  UserOrganizationListItem,
} from '@goatsports/core/data-access';
import { CurrentUserService } from '../../shared/services/current-user.service';
import { RouteConstants } from '../../shared/constants/route.constant';
import { LoadingStatus } from '@goatsports/shared/util';

type State = {
  organizations?: UserOrganizationListItem[];
  status: LoadingStatus;
  error?: string;
};

@Injectable({ providedIn: 'root' })
export class OrganizationAdminOrganizationsStore {
  private readonly organizationAdminsService = inject(OrganizationAdminsService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly router = inject(Router);

  private readonly INITIAL_STATE: State = {
    organizations: undefined,
    status: 'Success',
    error: undefined,
  };

  private readonly state = signal<State>(this.INITIAL_STATE);

  // selectors
  organizations = computed(() => this.state().organizations ?? []);
  status = computed(() => this.state().status);
  error = computed(() => this.state().error);
  organizationsCount = computed(() => (this.state().organizations ?? []).length);
  hasMultipleOrganizations = computed(() => this.organizationsCount() > 1);
  activeOrganization = computed(() =>
    (this.state().organizations ?? []).find((x) => !!x.isActive),
  );
  activeOrganizationUid = computed(
    () => this.activeOrganization()?.uid ?? undefined,
  );
  activeOrganizationName = computed(
    () => this.activeOrganization()?.name ?? undefined,
  );

  private get currentUserUid(): string | undefined {
    return this.currentUserService.currentUserUid as string | undefined;
  }

  private get currentUserRole(): RoleType | undefined {
    return this.currentUserService.currentUserRole as RoleType | undefined;
  }

  loadOrganizationsForCurrentAdmin(): void {
    if (this.currentUserRole !== RoleType.OrganizationAdmin) return;
    const uid = this.currentUserUid;
    if (!uid) {
      this.state.update((s) => ({ ...s, status: 'Error', error: 'Missing user id' }));
      return;
    }

    this.state.update((s) => ({ ...s, status: 'Loading', error: undefined }));

    this.organizationAdminsService
      .organizationAdminsUidOrganizationsGet(uid)
      .subscribe({
        next: (res: GetOrganizationAdminOrganizationsQueryResult) => {
          const items = res.items ?? [];
          this.state.update((s) => ({ ...s, organizations: items, status: 'Success' }));
        },
        error: (err) => {
          this.state.update((s) => ({ ...s, status: 'Error', error: err?.message ?? 'Failed to load organizations' }));
        },
      });
  }

  setActiveOrganization(organizationUid: string): void {
    if (this.currentUserRole !== RoleType.OrganizationAdmin) return;
    const uid = this.currentUserUid;
    if (!uid || !organizationUid) {
      return;
    }

    this.state.update((s) => ({ ...s, status: 'LoadingPartial', error: undefined }));

    const body: ChangeOrganizationAdminActiveOrganizationCommand = {
      organizationUid,
    } as ChangeOrganizationAdminActiveOrganizationCommand;

    this.organizationAdminsService
      .organizationAdminsUidActiveOrganizationPatch(uid, body)
      .subscribe({
        next: () => {
          // Hard redirect to Dashboard (respects base href) and reload in one go
          const url = this.router.serializeUrl(
            this.router.createUrlTree(['/', RouteConstants.DASHBOARD_ROUTE])
          );
          window.location.replace(url);
        },
        error: (err) => {
          this.state.update((s) => ({ ...s, status: 'Error', error: err?.message ?? 'Failed to update active organization' }));
        },
      });
  }
}
